import { Controller } from "@hotwired/stimulus";
import {
  createMap,
  fitBounds,
  removeMap,
  injectLayerControlStyle,
  createToggleButton,
  createTogglePanelContainer,
  MAP_TOGGLE_ICONS,
  hasTooltipValue,
  formatTooltipValue,
  openRouteTreeInNewTab,
} from "../../helpers/map_helpers.js";
import { escapeHtml } from "../../utils.js";

const ROUTE_WEIGHT = 3;
const SELECTED_ROUTE_WEIGHT = 4;
const ROUTE_OPACITY = 0.8;
const TREE_RADIUS = 6;
const TREE_WEIGHT = 2;
const TREE_FILL_OPACITY = 0.8;
const LOTS_LAYER_NAME = "🗺️ Lotes";
const ROUTES_LAYER_NAME = "🛤️ Rutas";
const HEATMAP_LAYER_PREFIX = "🔥 ";
const MARKER_LAYER_PREFIX = "📍 ";
const MARKER_CLUSTER_THRESHOLD = 500;

// Connects to data-controller="route-map-reports--map"
export default class extends Controller {
  static targets = [
    "data",
    "map",
    "pillLoading",
    "pillStatus",
    "pillClear",
    "drawerClear",
    "routeRow",
  ];

  static values = {
    farmLat: Number,
    farmLon: Number,
    lots: Array,
    selectedLotIds: Array,
    treeLabel: { type: String, default: "Tree" },
    tooltipMaxLabel: { type: String, default: "max" },
    tooltipAvgLabel: { type: String, default: "avg" },
    routeFieldAverageLabel: { type: String, default: "Average" },
    routeFieldRoutesLabel: { type: String, default: "Routes" },
    routeFieldNoDataLabel: { type: String, default: "No data" },
    routeFieldLowLabel: { type: String, default: "Low" },
    routeFieldHighLabel: { type: String, default: "High" },
    overviewUrl: String,
    fieldLayersUrl: String,
    routeGeometryUrl: String,
    selectedRouteId: { type: Number, default: 0 },
    pillHint: String,
    pillSelectedTemplate: String,
  };

  connect() {
    this.mapData = this.parseMapData();
    this.overviewRoutes = [];
    this.overviewLayers = new Map();
    this.detailLayer = null;
    this.selectedRouteId = null;
    this.fieldLayersLoaded = false;
    this.fieldLayersLoadPromise = null;
    this.fieldLayerPlaceholders = {};
    this.lastFieldLayerName = null;
    this.activeExclusiveLayerName = null;
    this.updateFieldsToggle = null;
    this.updateRoutesToggle = null;
    this.routeLayerGroup = L.layerGroup();
    this.overviewCanvasRenderer = L.canvas({padding: 0.5});

    this.initializeMap();
    this.renderLots();
    this.addControls();
    this.addRouteLotFieldLegend();
    this.fetchOverview();
  }

  disconnect() {
    removeMap(this.map);
    this.map = null;
  }

  parseMapData() {
    if (!this.hasDataTarget) return {};

    return JSON.parse(this.dataTarget.textContent);
  }

  initializeMap() {
    this.map = createMap(this.mapTarget, {
      lat: this.farmLatValue,
      lon: this.farmLonValue,
    });
  }

  async fetchOverview() {
    this.setPillLoading(true);

    try {
      const response = await fetch(this.overviewUrlValue, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(`Overview failed: ${response.status}`);

      const payload = await response.json();
      this.overviewRoutes = payload.routes || [];
      this.renderOverviewPaths();
      this.addTogglePanel();
      this.setPillHint();

      if (this.selectedRouteIdValue > 0) {
        await this.selectRouteById(this.selectedRouteIdValue);
      }
    } catch (error) {
      console.error(error);
      this.setPillHint();
    } finally {
      this.setPillLoading(false);
    }
  }

  renderOverviewPaths() {
    this.overviewLayers.forEach(({ layer }) =>
      this.routeLayerGroup.removeLayer(layer)
    );
    this.overviewLayers.clear();

    this.overviewRoutes.forEach((route) => {
      const color = this.routeColor(route);
      const layerGroup = L.layerGroup();
      let polyline = null;

      if (route.path && route.path.length > 1) {
        polyline = L.polyline(route.path, {
          color,
          weight: ROUTE_WEIGHT,
          opacity: ROUTE_OPACITY,
        });
        polyline.bindTooltip(`${route.serial} - ${route.lot_name}`, {
          sticky: true,
        });
        polyline.on("click", () => this.toggleRouteSelection(route.id));
        layerGroup.addLayer(polyline);
      }

      this.overviewTreeCoordinates(route).forEach((coordinates) => {
        const marker = L.circleMarker(coordinates, {
          radius: TREE_RADIUS,
          color,
          fillColor: color,
          fillOpacity: TREE_FILL_OPACITY,
          weight: TREE_WEIGHT,
          renderer: this.overviewCanvasRenderer,
        });
        marker.on("click", () => this.toggleRouteSelection(route.id));
        layerGroup.addLayer(marker);
      });

      this.routeLayerGroup.addLayer(layerGroup);
      this.overviewLayers.set(route.id, {layer: layerGroup, route, polyline});
    });

    this.syncRouteRowColors();
  }

  overviewTreeCoordinates(route) {
    return (route.trees || []).filter(
      (coordinates) => Array.isArray(coordinates) && coordinates.length >= 2
    );
  }

  toggleRouteSelection(routeId) {
    if (this.selectedRouteId === routeId) {
      this.clearSelection();
      return;
    }

    this.selectRouteById(routeId);
  }

  selectRoute(event) {
    this.closeRoutesDrawer();
    return this.selectRouteById(Number(event.params.routeId));
  }

  closeRoutesDrawer() {
    const toggle = document.getElementById("routes-drawer");
    if (toggle) toggle.checked = false;
  }

  async selectRouteById(routeId) {
    const route = this.overviewRoutes.find((item) => item.id === routeId);
    if (!route) return;

    this.showRoutesLayer();
    this.removeTreeDetail();
    this.selectedRouteId = routeId;
    this.isolateOverviewToRoute(routeId);
    this.highlightOverview(routeId);
    this.highlightRouteRow(routeId);
    this.setPillSelected(route.serial);
    this.syncSelectedRouteParam(routeId);
    await this.fetchTreeDetail(routeId);
  }

  clearSelection() {
    this.removeTreeDetail();
    this.showAllOverviewRoutes();
    this.resetOverviewStyles();
    this.selectedRouteId = null;
    this.highlightRouteRow(null);
    this.setPillHint();
    this.syncSelectedRouteParam(null);
  }

  showRoutesLayer() {
    if (!this.routeLayerGroup || !this.map) return;

    if (!this.map.hasLayer(this.routeLayerGroup)) {
      this.map.addLayer(this.routeLayerGroup);
      return;
    }

    if (this.activeExclusiveLayerName === ROUTES_LAYER_NAME) return;

    this.activeExclusiveLayerName = ROUTES_LAYER_NAME;
    this.hideOtherExclusiveLayers(ROUTES_LAYER_NAME);
    this.updateRoutesToggle?.(true);
    this.updateFieldsToggle?.(false);
  }

  isolateOverviewToRoute(routeId) {
    this.overviewLayers.forEach(({ layer }, id) => {
      if (id === routeId) {
        this.routeLayerGroup.addLayer(layer);
        return;
      }

      this.routeLayerGroup.removeLayer(layer);
    });
  }

  showAllOverviewRoutes() {
    this.overviewLayers.forEach(({ layer }) => {
      this.routeLayerGroup.addLayer(layer);
    });
  }

  async fetchTreeDetail(routeId) {
    try {
      const url = new URL(this.routeGeometryUrlValue, window.location.origin);
      url.searchParams.set("route_id", routeId);

      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        throw new Error(`Route geometry failed: ${response.status}`);
      }

      const route = await response.json();
      if (this.selectedRouteId !== routeId) return;

      this.renderTreeDetail(route);
    } catch (error) {
      console.error(error);
      if (this.selectedRouteId === routeId) this.clearSelection();
    }
  }

  renderTreeDetail(route) {
    this.removeTreeDetail();

    const layerGroup = L.layerGroup();

    const overviewRoute = this.overviewRoutes.find((item) => item.id === route.id);
    const color = this.routeColor(overviewRoute || route);

    if (route.path && route.path.length > 1) {
      const polyline = L.polyline(route.path, {
        color,
        weight: SELECTED_ROUTE_WEIGHT,
        opacity: 1,
      });
      polyline.bindTooltip(`${route.serial} - ${route.lot_name}`, {
        sticky: true,
      });
      layerGroup.addLayer(polyline);
      fitBounds(this.map, [polyline], { padding: [24, 24] });
    }

    (route.trees || []).forEach((tree) => {
      const marker = L.circleMarker(tree.coordinates, {
        radius: TREE_RADIUS,
        color,
        fillColor: color,
        fillOpacity: TREE_FILL_OPACITY,
        weight: TREE_WEIGHT,
      });
      marker.bindTooltip(this.treeTooltipContent(route, tree), {
        direction: "top",
      });
      marker.on("click", () => openRouteTreeInNewTab(tree));
      layerGroup.addLayer(marker);
    });

    const overview = this.overviewLayers.get(route.id);
    if (overview) this.routeLayerGroup.removeLayer(overview.layer);

    this.routeLayerGroup.addLayer(layerGroup);
    this.detailLayer = { layer: layerGroup, routeId: route.id };
  }

  removeTreeDetail() {
    if (!this.detailLayer) return;

    this.routeLayerGroup.removeLayer(this.detailLayer.layer);
    const overview = this.overviewLayers.get(this.detailLayer.routeId);
    if (overview) this.routeLayerGroup.addLayer(overview.layer);
    this.detailLayer = null;
  }

  highlightOverview(routeId) {
    this.overviewLayers.forEach(({ polyline, route }, id) => {
      if (!polyline) return;

      if (id === routeId) {
        polyline.setStyle({
          color: this.routeColor(route),
          opacity: 1,
          weight: SELECTED_ROUTE_WEIGHT,
        });
      } else {
        polyline.setStyle(this.overviewStyle(route));
      }
    });
  }

  resetOverviewStyles() {
    this.overviewLayers.forEach(({ polyline, route }) => {
      if (!polyline) return;

      polyline.setStyle(this.overviewStyle(route));
    });
  }

  overviewStyle(route) {
    return {
      color: this.routeColor(route),
      opacity: ROUTE_OPACITY,
      weight: ROUTE_WEIGHT,
    };
  }

  routeColor(route) {
    return route?.color || "#22c55e";
  }

  syncRouteRowColors() {
    if (!this.hasRouteRowTarget) return;

    this.routeRowTargets.forEach((row) => {
      const route = this.overviewRoutes.find(
        (item) => item.id === Number(row.dataset.routeId)
      );
      const dot = row.querySelector("[data-route-color-dot]");
      if (route?.color && dot) dot.style.backgroundColor = route.color;
    });
  }

  highlightRouteRow(routeId) {
    if (!this.hasRouteRowTarget) return;

    this.routeRowTargets.forEach((row) => {
      const selected = Number(row.dataset.routeId) === routeId;
      row.classList.toggle("bg-primary/5", selected);
      row.classList.toggle("border-primary", selected);
    });
  }

  syncSelectedRouteParam(routeId) {
    const input = document.getElementById("selected_route_id");
    if (input) input.value = routeId || "";

    const url = new URL(window.location.href);
    const previous = url.searchParams.get("selected_route_id") || "";
    const next = routeId ? String(routeId) : "";

    if (routeId) {
      url.searchParams.set("selected_route_id", routeId);
    } else {
      url.searchParams.delete("selected_route_id");
    }
    history.replaceState({}, "", url);

    if (previous !== next) this.reloadStatsFrame();
  }

  reloadStatsFrame() {
    const frame = document.getElementById("route_map_report_stats");
    if (!frame) return;

    frame.src = window.location.href;
  }

  setPillLoading(isLoading) {
    if (!this.hasPillLoadingTarget) return;
    this.pillLoadingTarget.classList.toggle("hidden", !isLoading);
  }

  setPillHint() {
    if (this.hasPillStatusTarget) {
      this.pillStatusTarget.textContent = this.pillHintValue;
    }
    this.setClearControlsVisible(false);
  }

  setPillSelected(serial) {
    if (this.hasPillStatusTarget) {
      this.pillStatusTarget.textContent = this.pillSelectedTemplateValue.replace(
        "__SERIAL__",
        serial
      );
    }
    this.setClearControlsVisible(true);
  }

  setClearControlsVisible(visible) {
    if (this.hasPillClearTarget) this.pillClearTarget.classList.toggle("hidden", !visible);
    if (this.hasDrawerClearTarget) this.drawerClearTarget.classList.toggle("hidden", !visible);
  }

  renderLots() {
    const selectedIds = this.selectedLotIdsValue || [];
    const selectedLotLayers = [];
    const routeLotFieldData = this.mapData?.route_lot_field_data;
    const lotFieldDataById = new Map(
      (routeLotFieldData?.lots || []).map((lot) => [Number(lot.lot_id), lot])
    );

    const lotLayers = this.lotsValue.map((lot) => {
      const isSelected = selectedIds.includes(lot.id);
      const lotFieldData = lotFieldDataById.get(Number(lot.id));

      const style = this.lotStyle({ isSelected, lotFieldData });

      const layer = L.geoJSON(JSON.parse(lot.polygon_geojson), {
        style: style,
      }).addTo(this.map);

      layer.bindTooltip(this.lotTooltipContent(lot, lotFieldData), {
        permanent: false,
        direction: "center",
        className: isSelected ? "lot-label-selected" : "lot-label",
      });

      if (isSelected) {
        selectedLotLayers.push(layer);
      }

      return layer;
    });

    const boundsLayers =
      selectedLotLayers.length > 0 ? selectedLotLayers : lotLayers;
    fitBounds(this.map, boundsLayers, { padding: [20, 20] });

    this.lotLayerGroup = L.layerGroup(lotLayers);
    this.lotLayerGroup.addTo(this.map);
  }

  lotStyle({ isSelected, lotFieldData }) {
    if (lotFieldData) {
      return {
        color: isSelected ? "#f59e0b" : "#78350f",
        weight: isSelected ? 3 : 1,
        fillOpacity: lotFieldData.has_value ? 0.58 : 0.14,
        fillColor: lotFieldData.color || "#94a3b8",
      };
    }

    return isSelected
      ? {
          color: "#f59e0b",
          weight: 3,
          fillOpacity: 0.1,
          fillColor: "#f59e0b",
        }
      : {
          color: "#3b82f6",
          weight: 1,
          fillOpacity: 0.05,
          fillColor: "#3b82f6",
        };
  }

  lotTooltipContent(lot, lotFieldData) {
    const name = escapeHtml(lot.name);
    if (!lotFieldData) return name;

    if (!lotFieldData.has_value) {
      return `${name}<br><span>${escapeHtml(this.routeFieldNoDataLabelValue)}</span>`;
    }

    return [
      name,
      `<br><span>${escapeHtml(this.routeFieldAverageLabelValue)}: ${escapeHtml(lotFieldData.value)}</span>`,
      `<br><span>${escapeHtml(this.routeFieldRoutesLabelValue)}: ${escapeHtml(lotFieldData.route_count)}</span>`,
    ].join("");
  }

  heatmapLayerName(fieldName) {
    return `${HEATMAP_LAYER_PREFIX}${fieldName}`;
  }

  markerLayerName(fieldName) {
    return `${MARKER_LAYER_PREFIX}${fieldName}`;
  }

  buildHeatLayer(customFieldName) {
    const values = this.mapData?.heatmap_data?.[customFieldName];
    if (!Array.isArray(values) || values.length === 0) return null;

    const heatmapPoints = values
      .filter(
        (point) =>
          Array.isArray(point) &&
          point.length === 3 &&
          typeof point[0] === "number" &&
          typeof point[1] === "number" &&
          typeof point[2] === "number" &&
          point[2] !== 0
      )
      .map(([lat, lon, value]) => [lat, lon, value]);

    if (heatmapPoints.length === 0) return null;

    const maxValue = Math.max(...heatmapPoints.map(([, , value]) => value));
    if (maxValue === 0) return null;

    try {
      return L.heatLayer(heatmapPoints, {
        radius: 15,
        blur: 20,
        minOpacity: 0.4,
        gradient: {
          0.2: "blue",
          0.4: "cyan",
          0.6: "lime",
          0.8: "yellow",
          1.0: "red",
        },
      });
    } catch (error) {
      console.error(`Error creating heatmap for ${customFieldName}:`, error);
      return null;
    }
  }

  buildMarkerLayer(customFieldName) {
    const values = this.mapData?.markers_data?.[customFieldName];
    if (!Array.isArray(values) || values.length === 0) return null;

    const useCluster = values.length > MARKER_CLUSTER_THRESHOLD;
    const layerGroup = useCluster
      ? L.markerClusterGroup({
          chunkedLoading: true,
          spiderfyOnMaxZoom: true,
          disableClusteringAtZoom: 18,
          maxClusterRadius: 40,
        })
      : L.layerGroup();

    values.forEach(({ coordinates, color }) => {
      const marker = L.circleMarker(coordinates, {
        radius: 5,
        color: color,
        fillColor: color,
        fillOpacity: 0.7,
        weight: 1,
      }).bindPopup(customFieldName);
      layerGroup.addLayer(marker);
    });

    return layerGroup;
  }

  addControls() {
    this.routeLayerGroup.addTo(this.map);
    this.activeExclusiveLayerName = ROUTES_LAYER_NAME;

    const overlays = {
      [LOTS_LAYER_NAME]: this.lotLayerGroup,
      [ROUTES_LAYER_NAME]: this.routeLayerGroup,
    };
    this.registerFieldLayerPlaceholders(overlays);

    this.layerControl = L.control
      .layers(null, overlays, {
        collapsed: true,
        sortLayers: false,
      })
      .addTo(this.map);

    injectLayerControlStyle();
    this.map.on("overlayadd", (event) => this.handleOverlayAdd(event));
    this.map.on("overlayremove", (event) => this.handleOverlayRemove(event));
    this.bindExclusiveFieldLayerClicks();
  }

  registerFieldLayerPlaceholders(overlays) {
    const catalog = this.mapData?.field_layer_catalog || {};
    const heatmaps = catalog.heatmaps || [];
    const markers = catalog.markers || [];

    heatmaps.forEach((fieldName) => {
      const name = this.heatmapLayerName(fieldName);
      const layer = L.layerGroup();
      this.fieldLayerPlaceholders[name] = {
        kind: "heatmap",
        fieldName,
        layer,
        populated: false,
      };
      overlays[name] = layer;
    });

    markers.forEach((fieldName) => {
      const name = this.markerLayerName(fieldName);
      const layer = L.layerGroup();
      this.fieldLayerPlaceholders[name] = {
        kind: "marker",
        fieldName,
        layer,
        populated: false,
      };
      overlays[name] = layer;
    });
  }

  isFieldLayerName(name) {
    return Boolean(this.fieldLayerPlaceholders[name]);
  }

  isRoutesLayerName(name) {
    return name === ROUTES_LAYER_NAME;
  }

  isExclusiveLayerName(name) {
    return this.isRoutesLayerName(name) || this.isFieldLayerName(name);
  }

  async handleOverlayAdd(event) {
    if (!this.isExclusiveLayerName(event.name)) return;

    this.activeExclusiveLayerName = event.name;
    this.queueExclusiveLayers(event.name);

    if (this.isRoutesLayerName(event.name)) {
      this.updateRoutesToggle?.(true);
      this.updateFieldsToggle?.(false);
      return;
    }

    this.lastFieldLayerName = event.name;
    this.updateFieldsToggle?.(true);
    this.updateRoutesToggle?.(false);
    await this.ensureFieldLayerPopulated(event.name);
  }

  handleOverlayRemove(event) {
    if (!this.isExclusiveLayerName(event.name)) return;

    if (this.isRoutesLayerName(event.name)) {
      this.updateRoutesToggle?.(false);
      if (this.activeExclusiveLayerName === event.name) {
        this.activeExclusiveLayerName = null;
      }
      return;
    }

    if (this.activeExclusiveLayerName !== event.name) return;

    this.activeExclusiveLayerName = null;
    this.updateFieldsToggle?.(false);
    this.restoreRoutesLayer();
  }

  queueExclusiveLayers(selectedName) {
    this.pendingExclusiveLayerName = selectedName;
    if (this.exclusiveLayersQueued) return;

    this.exclusiveLayersQueued = true;
    queueMicrotask(() => {
      this.exclusiveLayersQueued = false;
      const name = this.pendingExclusiveLayerName;
      this.pendingExclusiveLayerName = null;
      if (!name || this.activeExclusiveLayerName !== name) return;
      this.hideOtherExclusiveLayers(name);
    });
  }

  restoreRoutesLayer() {
    if (!this.routeLayerGroup || this.map.hasLayer(this.routeLayerGroup)) return;
    this.map.addLayer(this.routeLayerGroup);
  }

  bindExclusiveFieldLayerClicks() {
    const container = this.layerControl?.getContainer?.();
    if (!container) return;

    container.addEventListener(
      "click",
      (event) => {
        const input = event.target?.closest?.(".leaflet-control-layers-selector");
        if (!input || input.type !== "checkbox" || !input.checked) return;

        const name = input.closest("label")?.textContent.trim();
        if (!this.isExclusiveLayerName(name)) return;

        this.uncheckOtherExclusiveInputs(name);
      },
      true
    );
  }

  uncheckOtherExclusiveInputs(selectedName) {
    const container = this.layerControl?.getContainer?.();
    if (!container) return;

    container
      .querySelectorAll(".leaflet-control-layers-overlays label")
      .forEach((label) => {
        const name = label.textContent.trim();
        if (name === selectedName || !this.isExclusiveLayerName(name)) return;

        const input = label.querySelector("input");
        if (input) input.checked = false;
      });
  }

  hideOtherExclusiveLayers(selectedName) {
    Object.entries(this.fieldLayerPlaceholders).forEach(([name, meta]) => {
      if (name === selectedName) return;
      if (this.map.hasLayer(meta.layer)) {
        this.map.removeLayer(meta.layer);
      }
    });

    if (
      selectedName !== ROUTES_LAYER_NAME &&
      this.routeLayerGroup &&
      this.map.hasLayer(this.routeLayerGroup)
    ) {
      this.map.removeLayer(this.routeLayerGroup);
    }

    this.syncExclusiveOverlayInputs(selectedName);
  }

  syncExclusiveOverlayInputs(selectedName) {
    const container = this.layerControl?.getContainer?.();
    if (!container) return;

    container
      .querySelectorAll(".leaflet-control-layers-overlays label")
      .forEach((label) => {
        const name = label.textContent.trim();
        if (!this.isExclusiveLayerName(name)) return;

        const input = label.querySelector("input");
        if (input) input.checked = name === selectedName;
      });
  }

  async ensureFieldLayerPopulated(layerName) {
    await this.loadFieldLayers();
    if (this.activeExclusiveLayerName !== layerName) return;
    this.populateFieldLayer(layerName);
  }

  populateFieldLayer(layerName) {
    const meta = this.fieldLayerPlaceholders[layerName];
    if (!meta || meta.populated) return;

    const content =
      meta.kind === "heatmap"
        ? this.buildHeatLayer(meta.fieldName)
        : this.buildMarkerLayer(meta.fieldName);

    if (content) meta.layer.addLayer(content);

    meta.populated = true;
  }

  addTogglePanel() {
    const hasRoutes = this.overviewLayers.size > 0;
    const hasFieldLayers = Object.keys(this.fieldLayerPlaceholders).length > 0;
    if (!hasRoutes && !hasFieldLayers) return;

    const TogglePanel = L.Control.extend({
      options: { position: "bottomleft" },

      onAdd: () => {
        const container = createTogglePanelContainer();
        let btnIndex = 0;

        if (hasRoutes) {
          this.updateRoutesToggle = createToggleButton(container, {
            icon: MAP_TOGGLE_ICONS.route,
            label: "Rutas",
            title: "Mostrar/ocultar todas las rutas",
            activeColor: "#10b981",
            visible: this.map.hasLayer(this.routeLayerGroup),
            isFirst: btnIndex++ === 0,
            onToggle: () => {
              if (this.map.hasLayer(this.routeLayerGroup)) {
                this.map.removeLayer(this.routeLayerGroup);
              } else {
                this.map.addLayer(this.routeLayerGroup);
              }
            },
          });
        }

        if (hasFieldLayers) {
          this.updateFieldsToggle = createToggleButton(container, {
            icon: MAP_TOGGLE_ICONS.fields,
            label: "Campos",
            title: "Mostrar/ocultar campos de datos",
            activeColor: "#3b82f6",
            visible: this.isFieldLayerName(this.activeExclusiveLayerName),
            isFirst: btnIndex++ === 0,
            onToggle: async (update) => {
              await this.toggleActiveFieldLayer();
              update(this.isFieldLayerName(this.activeExclusiveLayerName));
            },
          });
        }

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    this.map.addControl(new TogglePanel());
  }

  async toggleActiveFieldLayer() {
    if (this.fieldLayersLoadPromise) return;

    if (this.isFieldLayerName(this.activeExclusiveLayerName)) {
      const meta = this.fieldLayerPlaceholders[this.activeExclusiveLayerName];
      if (meta && this.map.hasLayer(meta.layer)) {
        this.map.removeLayer(meta.layer);
      }
      return;
    }

    if (
      this.lastFieldLayerName &&
      this.fieldLayerPlaceholders[this.lastFieldLayerName]
    ) {
      this.map.addLayer(
        this.fieldLayerPlaceholders[this.lastFieldLayerName].layer
      );
      return;
    }

    this.layerControl?.expand();
  }

  async loadFieldLayers() {
    if (this.fieldLayersLoaded) return;
    if (this.fieldLayersLoadPromise) return this.fieldLayersLoadPromise;

    this.fieldLayersLoadPromise = this.fetchFieldLayers();
    try {
      await this.fieldLayersLoadPromise;
    } finally {
      this.fieldLayersLoadPromise = null;
    }
  }

  async fetchFieldLayers() {
    if (!this.fieldLayersUrlValue) return;

    const response = await fetch(this.fieldLayersUrlValue, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return;

    const payload = await response.json();
    this.mapData = {
      ...this.mapData,
      heatmap_data: payload.heatmap_data,
      markers_data: payload.markers_data,
    };
    this.fieldLayersLoaded = true;
  }

  addRouteLotFieldLegend() {
    const routeLotFieldData = this.mapData?.route_lot_field_data;
    const legend = routeLotFieldData?.legend;
    if (!routeLotFieldData?.field || !legend?.colors?.length) return;

    const LegendControl = L.Control.extend({
      options: { position: "bottomright" },

      onAdd: () => {
        const container = L.DomUtil.create(
          "div",
          "leaflet-control route-lot-field-legend"
        );
        const colors = legend.colors
          .map(
            (color) =>
              `<span style="display:block;width:18px;height:10px;background:${escapeHtml(color)}"></span>`
          )
          .join("");
        const lowValue =
          legend.min === null || legend.min === undefined ? "-" : legend.min;
        const highValue =
          legend.max === null || legend.max === undefined ? "-" : legend.max;

        container.style.cssText = `
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.2);
          padding: 8px 10px;
          color: #111827;
          font-size: 12px;
          min-width: 150px;
        `;
        container.innerHTML = `
          <div style="font-weight:600;margin-bottom:6px;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
            ${escapeHtml(routeLotFieldData.field.name)}
          </div>
          <div style="display:flex;border-radius:4px;overflow:hidden;margin-bottom:4px;">${colors}</div>
          <div style="display:flex;justify-content:space-between;gap:12px;color:#4b5563;font-size:11px;">
            <span>${escapeHtml(this.routeFieldLowLabelValue)} ${escapeHtml(lowValue)}</span>
            <span>${escapeHtml(this.routeFieldHighLabelValue)} ${escapeHtml(highValue)}</span>
          </div>
        `;

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        return container;
      },
    });

    this.map.addControl(new LegendControl());
  }

  treeTooltipContent(route, tree) {
    const label = `${route.serial} - ${this.treeLabelValue} #${tree.tree_number}`;
    const labels = {
      maxLabel: this.tooltipMaxLabelValue,
      avgLabel: this.tooltipAvgLabelValue,
    };
    const tooltipRows = Object.entries(tree.descriptive_values || {})
      .filter(([, value]) => hasTooltipValue(value))
      .map(
        ([key, value]) =>
          `<li>${escapeHtml(key)}: ${escapeHtml(formatTooltipValue(value, labels))}</li>`
      )
      .join("");

    if (!tooltipRows) return escapeHtml(label);

    return `${escapeHtml(label)}<ul>${tooltipRows}</ul>`;
  }
}
